import { Dispatch, SetStateAction } from 'react'
import Delete from '@mui/icons-material/Delete'
import Download from '@mui/icons-material/Download'
import Edit from '@mui/icons-material/Edit'
import Rule from '@mui/icons-material/Rule'
import { Divider, ListItemIcon, MenuItem } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { useRunAllRulesWithDocChange } from '@/hooks/doc-changes-wrapper-hooks'
import useOverlay from '@/hooks/useOverlay'
import { useTriggerWorkflowStateEvent } from '@/service-library/hooks/events'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { SUPER_USER_ONLY_COLOR } from '@/theme/usePixydocsTheme'
import downloadFile from '@/utils/download-file'
import { showErrorSnackbar, showInfoSnackbar } from '@/utils/snackbars'
import BulkDocumentDialog from '@/components/bulk-document-dialog/BulkDocumentDialog'
import {
  SystemEventIcon,
  useSystemEventTriggers,
} from '@/components/document-actions-menu/system-event-triggers'
import IconButtonMenu from '@/components/icon-button-menu/IconButtonMenu'
import { useNotifications } from '@/components/notifications/NotificationProvider'
import DeleteDocumentsDialog from './DeleteDocumentsDialog'
import DownloadOriginalDocumentsDialog from './DownloadOriginalDocumentsDialog'
import { useSelectedWorkflowStateContext } from './SelectedWorkflowStateProvider'

type DocumentGridActionsMenuProps = {
  dataQueryKey: QueryKey
  selectedDocumentIds: string[]
  queryBy: 'rows' | 'documents'
  setSelectedRows: Dispatch<SetStateAction<Record<string, boolean>>>
  refetchDocuments: () => void
}

export default function DocumentGridActionsMenu({
  dataQueryKey,
  selectedDocumentIds,
  queryBy,
  setSelectedRows,
  refetchDocuments,
}: DocumentGridActionsMenuProps) {
  const menuOverlay = useOverlay()
  const bulkDocumentOverlay = useOverlay()
  const deleteDocsOverlay = useOverlay()
  const downloadOriginalDocsOverlay = useOverlay()
  const isSuperUser = useIsSuperUser()

  const { selectedWorkflowState } = useSelectedWorkflowStateContext()

  const systemEventTriggers = useSystemEventTriggers({
    workflowStateId: selectedWorkflowState?.id,
    enabled: isSuperUser,
  })

  const { triggerWorkflowStateEvent } = useTriggerWorkflowStateEvent()

  const { runRules } = useRunAllRulesWithDocChange({
    documentIds: selectedDocumentIds,
    workflowId: selectedWorkflowState?.workflow_id || '',
    workflowStateId: selectedWorkflowState?.id || '',
    onError: () => {
      showErrorSnackbar('Documents validation failed. Please try again later.')
    },
  })

  const numberOfSelectedRows = selectedDocumentIds.length
  const disableAction = numberOfSelectedRows === 0

  function handleClick(onClick: () => void) {
    menuOverlay.close()
    onClick()
  }

  function triggerEvent(workflowStateEventId: string) {
    triggerWorkflowStateEvent(
      selectedDocumentIds.map((id) => ({
        workflow_state_event_id: workflowStateEventId,
        document_id: id,
        status: 'waiting',
      })),
    )
    menuOverlay.close()
  }

  // This is just a temporary solution for super users only
  async function bulkRunRules() {
    showInfoSnackbar('Validating Documents')
    setSelectedRows({})
    runRules(true)
  }

  useNotifications({
    keys: ['export_original_complete', 'export_original_failed'],
    callback: ({ action, export_response, error }) => {
      if (action === 'export_original_failed') {
        showErrorSnackbar(
          'Failed to download documents. Please try again later.',
        )
      } else {
        if (export_response) {
          downloadFile(
            export_response.file_url,
            export_response.file_name,
          ).catch(() => {
            showErrorSnackbar(
              'Failed to download documents. Please try again later.',
            )
          })
        }
        if (error === 'not-found') {
          showErrorSnackbar(
            'Some documents failed to be included in the download. Please contact support.',
          )
        }
      }
    },
  })

  return (
    <>
      <IconButtonMenu overlay={menuOverlay}>
        <MenuItem
          onClick={() => handleClick(bulkDocumentOverlay.open)}
          disabled={disableAction}
        >
          <ListItemIcon>
            <Edit />
          </ListItemIcon>
          Edit Documents{' '}
          {numberOfSelectedRows ? `(${numberOfSelectedRows})` : ''}
        </MenuItem>
        <MenuItem
          onClick={() => handleClick(deleteDocsOverlay.open)}
          disabled={disableAction}
        >
          <ListItemIcon>
            <Delete />
          </ListItemIcon>
          Delete Documents{' '}
          {numberOfSelectedRows ? `(${numberOfSelectedRows})` : ''}
        </MenuItem>
        <MenuItem
          onClick={() => handleClick(downloadOriginalDocsOverlay.open)}
          disabled={disableAction}
        >
          <ListItemIcon>
            <Download />
          </ListItemIcon>
          Download Original Files{' '}
          {numberOfSelectedRows ? `(${numberOfSelectedRows})` : ''}
        </MenuItem>
        {isSuperUser && selectedWorkflowState && (
          <>
            <Divider />
            <MenuItem
              onClick={() => handleClick(bulkRunRules)}
              disabled={disableAction}
              sx={{ color: SUPER_USER_ONLY_COLOR }}
            >
              <ListItemIcon>
                <Rule
                  sx={{
                    color: SUPER_USER_ONLY_COLOR,
                  }}
                />
              </ListItemIcon>
              Run Rules{' '}
              {numberOfSelectedRows ? `(${numberOfSelectedRows})` : ''} (Clear
              Cache)
            </MenuItem>
            {systemEventTriggers.map(({ id, code, name }) => (
              <MenuItem
                key={id}
                onClick={() => {
                  triggerEvent(id)
                }}
                disabled={disableAction}
                sx={{ color: SUPER_USER_ONLY_COLOR }}
              >
                <ListItemIcon>
                  <SystemEventIcon code={code} />
                </ListItemIcon>
                Run {name} Event{' '}
                {numberOfSelectedRows ? `(${numberOfSelectedRows})` : ''}{' '}
              </MenuItem>
            ))}
          </>
        )}
      </IconButtonMenu>

      {bulkDocumentOverlay.isOpen && (
        <BulkDocumentDialog
          dataQueryKey={dataQueryKey}
          documentIds={selectedDocumentIds}
          overlay={bulkDocumentOverlay}
          queryBy={queryBy}
          setSelectedRows={setSelectedRows}
        />
      )}

      <DeleteDocumentsDialog
        overlay={deleteDocsOverlay}
        documentIds={selectedDocumentIds}
        onDeleteSettled={() => {
          setSelectedRows({})
          refetchDocuments()
        }}
      />

      <DownloadOriginalDocumentsDialog
        overlay={downloadOriginalDocsOverlay}
        documentIds={selectedDocumentIds}
        setSelectedRows={setSelectedRows}
      />
    </>
  )
}
